import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AlertCircle, Mic, Square, Play, Pause, Download, Clipboard, FileDown } from 'lucide-react';

// Import API_URL from config file
import API_URL from '../config';
import logger from '../utils/logger';

const MindsetScript = () => {
  const [mindset, setMindset] = useState({
    oneWordFocus: '',
    keysToKeepItSimple: [],
    affirmations: [],
    identityStatement: {
      iHave: [],
      iAm: [],
      iLove: [],
      iGive: []
    }
  });
  
  const [script, setScript] = useState('');
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  
  // Show notification
  const showNotification = (message, isError = false) => {
    setNotification({
      show: true,
      message,
      type: isError ? 'error' : 'success'
    });
    
    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };
  
  // Fetch mindset data
  useEffect(() => {
    const fetchMindset = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('Authentication required. Please log in again.');
        }
        
        logger.debug('Fetching mindset data from:', `${API_URL}/mindset`);
        
        const response = await axios.get(`${API_URL}/mindset`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        logger.debug('Mindset data response:', response.data);
        
        // Ensure all properties exist in the response
        const mindsetData = {
          oneWordFocus: response.data.oneWordFocus || '',
          keysToKeepItSimple: response.data.keysToKeepItSimple || [],
          affirmations: response.data.affirmations || [],
          identityStatement: {
            iHave: response.data.identityStatement?.iHave || [],
            iAm: response.data.identityStatement?.iAm || [],
            iLove: response.data.identityStatement?.iLove || [],
            iGive: response.data.identityStatement?.iGive || []
          }
        };
        
        setMindset(mindsetData);
        setLoading(false);
      } catch (err) {
        logger.error('Error fetching mindset data:', err);
        setError('Failed to load your mindset data: ' + (err.message || 'Unknown error'));
        setLoading(false);
      }
    };
    
    fetchMindset();
  }, []);
  
  // Generate script when mindset data changes
  useEffect(() => {
    generateScript();
  }, [mindset]);
  
  // Handle audio playback
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => {
        setIsPlaying(false);
      };
    }
  }, [audioURL]);
  
  const generateScript = () => {
    let scriptText = '';
    
    // Opening
    scriptText += "Let me take a moment to reset my mindset and remind myself of what's most important.\n\n";
    
    // One Word Focus
    if (mindset.oneWordFocus) {
      scriptText += `My one word focus is ${mindset.oneWordFocus.toUpperCase()}. This word is my anchor and guide.\n\n`;
    }
    
    // Keys to Keep it Simple
    if (mindset.keysToKeepItSimple && mindset.keysToKeepItSimple.length > 0) {
      scriptText += "To keep things simple and within my control, I remember:\n";
      mindset.keysToKeepItSimple.forEach(key => {
        if (key && key.trim()) scriptText += `- ${key}\n`;
      });
      scriptText += "\n";
    }
    
    // Affirmations
    if (mindset.affirmations && mindset.affirmations.length > 0) {
      scriptText += "I affirm that:\n";
      mindset.affirmations.forEach(affirmation => {
        if (affirmation && affirmation.trim()) scriptText += `- ${affirmation}\n`;
      });
      scriptText += "\n";
    }
    
    // Identity Statements
    scriptText += "This is who I am:\n\n";
    
    if (mindset.identityStatement && mindset.identityStatement.iAm && mindset.identityStatement.iAm.length > 0) {
      mindset.identityStatement.iAm.forEach(statement => {
        if (statement && statement.trim()) scriptText += `I am ${statement}.\n`;
      });
      scriptText += "\n";
    }
    
    if (mindset.identityStatement && mindset.identityStatement.iHave && mindset.identityStatement.iHave.length > 0) {
      mindset.identityStatement.iHave.forEach(statement => {
        if (statement && statement.trim()) scriptText += `I have ${statement}.\n`;
      });
      scriptText += "\n";
    }
    
    if (mindset.identityStatement && mindset.identityStatement.iLove && mindset.identityStatement.iLove.length > 0) {
      mindset.identityStatement.iLove.forEach(statement => {
        if (statement && statement.trim()) scriptText += `I love ${statement}.\n`;
      });
      scriptText += "\n";
    }
    
    if (mindset.identityStatement && mindset.identityStatement.iGive && mindset.identityStatement.iGive.length > 0) {
      mindset.identityStatement.iGive.forEach(statement => {
        if (statement && statement.trim()) scriptText += `I give ${statement}.\n`;
      });
      scriptText += "\n";
    }
    
    // Closing
    scriptText += "I am focused, clear, and ready to perform at my best.";
    
    setScript(scriptText);
  };
  
  // Updated startRecording function with diagnostics
  const startRecording = async () => {
    try {
      // Diagnostic logging
      logger.debug('======= RECORDING DIAGNOSTICS =======');
      logger.debug('Browser:', navigator.userAgent);
      logger.debug('MediaRecorder support:', !!window.MediaRecorder);
      logger.debug('getUserMedia support:', !!navigator.mediaDevices?.getUserMedia);
      
      // Check supported MIME types
      if (window.MediaRecorder && MediaRecorder.isTypeSupported) {
        logger.debug('audio/webm support:', MediaRecorder.isTypeSupported('audio/webm'));
        logger.debug('audio/mp4 support:', MediaRecorder.isTypeSupported('audio/mp4'));
        logger.debug('audio/ogg support:', MediaRecorder.isTypeSupported('audio/ogg'));
      }
      
      logger.debug('Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      logger.debug('Microphone access granted');
      
      // Check if we actually got audio tracks
      const audioTracks = stream.getAudioTracks();
      logger.debug('Audio tracks received:', audioTracks.length);
      if (audioTracks.length > 0) {
        logger.debug('Audio track settings:', audioTracks[0].getSettings());
      }
      
      if (!audioTracks.length) {
        throw new Error('No audio tracks were received from the microphone');
      }
      
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        throw new Error('MediaRecorder API is not supported in this browser');
      }
      
      let options = {};
      
      // Try to use common audio formats with fallbacks
      try {
        if (MediaRecorder.isTypeSupported('audio/webm')) {
          options = { mimeType: 'audio/webm' };
          logger.debug('Using audio/webm format');
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          options = { mimeType: 'audio/mp4' };
          logger.debug('Using audio/mp4 format');
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          options = { mimeType: 'audio/ogg' };
          logger.debug('Using audio/ogg format');
        } else {
          logger.debug('No specific MIME type supported, using default');
        }
      } catch (e) {
        logger.warn('Error checking MIME type support:', e);
        logger.debug('Using default MIME type');
      }
      
      logger.debug('Creating MediaRecorder with options:', options);
      try {
        mediaRecorderRef.current = new MediaRecorder(stream, options);
        logger.debug('MediaRecorder created successfully');
        logger.debug('MediaRecorder state:', mediaRecorderRef.current.state);
      } catch (e) {
        logger.warn('Error with specified options, trying without options');
        mediaRecorderRef.current = new MediaRecorder(stream);
        logger.debug('MediaRecorder created with default options');
      }
      
      chunksRef.current = [];
      let hasReceivedData = false;
      
      logger.debug('Setting up ondataavailable handler');
      mediaRecorderRef.current.ondataavailable = (e) => {
        logger.debug('Data available event fired, data size:', e.data.size);
        if (e.data.size > 0) {
          hasReceivedData = true;
          chunksRef.current.push(e.data);
        }
      };
      
      logger.debug('Setting up onstop handler');
      mediaRecorderRef.current.onstop = () => {
        logger.debug('MediaRecorder stopped');
        logger.debug('Total chunks collected:', chunksRef.current.length);
        logger.debug('Has received data:', hasReceivedData);
        
        try {
          // When recording stops, create the audio blob
          const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
          logger.debug('Creating blob with MIME type:', mimeType);
          const blob = new Blob(chunksRef.current, { type: mimeType });
          logger.debug('Blob created, size:', blob.size);
          setAudioBlob(blob);
          const url = URL.createObjectURL(blob);
          setAudioURL(url);
          setRecording(false);
          showNotification('Recording saved successfully');
          
          // Stop all tracks in the stream to release the microphone
          logger.debug('Releasing microphone');
          stream.getTracks().forEach(track => track.stop());
        } catch (err) {
          logger.error('Error finalizing recording:', err);
          showNotification('Error saving recording: ' + err.message, true);
        }
      };
      
      logger.debug('Starting MediaRecorder');
      mediaRecorderRef.current.start(1000); // Request data every second
      logger.debug('MediaRecorder started, state:', mediaRecorderRef.current.state);
      setRecording(true);
      showNotification('Recording started');
      
      // Add periodic checks for data
      setTimeout(() => {
        logger.debug('Recording for 2 seconds, has received data:', hasReceivedData);
        logger.debug('Current state:', mediaRecorderRef.current.state);
      }, 2000);
      
    } catch (err) {
      logger.error('Error starting recording:', err);
      showNotification('Could not access microphone: ' + err.message, true);
    }
  };
  
  // Updated stopRecording function with diagnostics
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      try {
        logger.debug('Stopping MediaRecorder');
        logger.debug('Current state before stop:', mediaRecorderRef.current.state);
        mediaRecorderRef.current.stop();
        logger.debug('MediaRecorder stop() called');
      } catch (err) {
        logger.error('Error stopping recording:', err);
        showNotification('Error stopping recording: ' + err.message, true);
        setRecording(false);
      }
    } else {
      logger.debug('Cannot stop recording - mediaRecorder not available or not recording');
      if (!mediaRecorderRef.current) {
        logger.debug('mediaRecorderRef.current is null or undefined');
      }
      if (!recording) {
        logger.debug('recording state variable is false');
      }
    }
  };
  
  const playAudio = () => {
    if (audioRef.current && audioURL) {
      try {
        audioRef.current.play();
        setIsPlaying(true);
      } catch (err) {
        logger.error('Error playing audio:', err);
        showNotification('Error playing audio: ' + err.message, true);
      }
    } else {
      showNotification('No recording available to play', true);
    }
  };
  
  const pauseAudio = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        setIsPlaying(false);
      } catch (err) {
        logger.error('Error pausing audio:', err);
        showNotification('Error pausing audio: ' + err.message, true);
      }
    }
  };
  
  const copyToClipboard = () => {
    try {
      navigator.clipboard.writeText(script)
        .then(() => {
          showNotification('Script copied to clipboard');
        })
        .catch((err) => {
          logger.error('Error copying to clipboard:', err);
          showNotification('Failed to copy script: ' + err.message, true);
        });
    } catch (err) {
      logger.error('Error accessing clipboard API:', err);
      
      // Fallback method if clipboard API is not available
      try {
        const textArea = document.createElement('textarea');
        textArea.value = script;
        document.body.appendChild(textArea);
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          showNotification('Script copied to clipboard');
        } else {
          showNotification('Failed to copy script', true);
        }
      } catch (fallbackErr) {
        showNotification('Failed to copy: ' + fallbackErr.message, true);
      }
    }
  };
  
  const downloadRecording = () => {
    if (audioBlob) {
      try {
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';
        
        const url = URL.createObjectURL(audioBlob);
        a.href = url;
        
        // Use the MIME type from the recorder if available, or fallback to webm
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const extension = mimeType.split('/')[1] || 'webm';
        a.download = `mindset-script-recording.${extension}`;
        a.click();
        
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        showNotification('Recording downloaded');
      } catch (err) {
        logger.error('Error downloading recording:', err);
        showNotification('Error downloading recording: ' + err.message, true);
      }
    } else {
      showNotification('No recording available to download', true);
    }
  };
  
  const downloadScript = () => {
    try {
      const element = document.createElement('a');
      const file = new Blob([script], { type: 'text/plain' });
      element.href = URL.createObjectURL(file);
      element.download = 'mindset-script.txt';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      showNotification('Script downloaded');
    } catch (err) {
      logger.error('Error downloading script:', err);
      showNotification('Failed to download script: ' + err.message, true);
    }
  };
  
  // Notification component
  const Notification = () => {
    if (!notification.show) return null;
    
    return (
      <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${
        notification.type === 'error' 
          ? 'bg-red-100 text-red-700 border border-red-400' 
          : 'bg-green-100 text-green-700 border border-green-400'
      }`}>
        <p>{notification.message}</p>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <div className="flex items-center">
            <AlertCircle className="mr-2" size={20} />
            <p>{error}</p>
          </div>
          <button 
            className="mt-4 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded" 
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6">Your Mindset Script</h1>
      
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Read Your Script</h2>
          <div className="flex space-x-2">
            <button 
              onClick={copyToClipboard}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 p-2 rounded flex items-center"
              title="Copy to clipboard"
            >
              <Clipboard className="h-5 w-5" />
              <span className="ml-1 hidden sm:inline">Copy</span>
            </button>
            <button 
              onClick={downloadScript}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 p-2 rounded flex items-center"
              title="Download script"
            >
              <FileDown className="h-5 w-5" />
              <span className="ml-1 hidden sm:inline">Download</span>
            </button>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-line h-64 overflow-y-auto border border-gray-200">
          {script || "No script generated. Please check your mindset settings."}
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Record Your Script</h2>
        
        <div className="flex flex-wrap justify-center gap-4 mb-6">
          {!recording && !audioURL && (
            <button
              onClick={startRecording}
              className="flex items-center justify-center bg-red-500 hover:bg-red-600 text-white py-3 px-6 rounded-full"
            >
              <Mic className="h-5 w-5" />
              <span className="ml-2">Start Recording</span>
            </button>
          )}
          
          {recording && (
            <button
              onClick={stopRecording}
              className="flex items-center justify-center bg-gray-700 hover:bg-gray-800 text-white py-3 px-6 rounded-full"
            >
              <Square className="h-5 w-5" />
              <span className="ml-2">Stop Recording</span>
            </button>
          )}
          
          {audioURL && !isPlaying && (
            <button
              onClick={playAudio}
              className="flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-full"
            >
              <Play className="h-5 w-5" />
              <span className="ml-2">Play Recording</span>
            </button>
          )}
          
          {audioURL && isPlaying && (
            <button
              onClick={pauseAudio}
              className="flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-full"
            >
              <Pause className="h-5 w-5" />
              <span className="ml-2">Pause</span>
            </button>
          )}
          
          {audioURL && (
            <button
              onClick={downloadRecording}
              className="flex items-center justify-center bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-full"
            >
              <Download className="h-5 w-5" />
              <span className="ml-2">Download Recording</span>
            </button>
          )}
        </div>
        
        {recording && (
          <div className="text-center bg-red-50 py-2 px-4 rounded-lg border border-red-200 mb-4">
            <div className="flex items-center justify-center">
              <div className="animate-pulse h-3 w-3 bg-red-500 rounded-full mr-2"></div>
              <p className="text-red-700">Recording in progress...</p>
            </div>
          </div>
        )}
        
        {audioURL && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-4">
            <audio ref={audioRef} src={audioURL} className="w-full" controls />
          </div>
        )}
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <h2 className="text-xl font-semibold mb-4">Tips for Recording</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Find a quiet place with minimal background noise</li>
          <li>Speak slowly and clearly</li>
          <li>Take deep breaths between sections</li>
          <li>Read with conviction and belief in your words</li>
          <li>Try to record when you're feeling positive and energized</li>
          <li>Listen to your recording regularly to reinforce your mindset</li>
        </ul>
      </div>
      
      <Notification />
    </div>
  );
};

export default MindsetScript;